// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useMemo } from 'react';
import NavigationPanel from './NavigationPanel';
import ShipManagementScreen from './ShipManagementScreen';
import CrewManagementScreen from './CrewManagementScreen';
import TradeScreen from './TradeScreen';
import MissionsTab from './InfoPanelTabs/MissionsTab';
import StatsTab from './InfoPanelTabs/StatsTab';
import MoreTab from './MoreTab';
import useGlobalHotkey from '../hooks/useGlobalHotkey';
import './MainTabbedInterface.css';

/**
 * Main Tabbed Interface
 * Single tab system for entire game
 */
const MainTabbedInterface = () => {
  const [activeTab, setActiveTab] = useState('nav');

  const tabs = [
    { id: 'nav', label: 'NAV', icon: '🗺️', hotkey: 'n' },
    { id: 'ship', label: 'SHIP', icon: '🚀', hotkey: 'h' },
    { id: 'crew', label: 'CREW', icon: '👥', hotkey: 'c' },
    { id: 'cargo', label: 'CARGO', icon: '📦', hotkey: 'g' },
    { id: 'missions', label: 'MISSIONS', icon: '📋', hotkey: 'm' },
    { id: 'stats', label: 'STATS', icon: '📊', hotkey: 't' },
    { id: 'more', label: 'MORE', icon: '⚙️', hotkey: 'o' }
  ];

  // Keyboard shortcuts — guard against typing-in-a-field and open overlays
  // is handled centrally by useGlobalHotkey (see OverlayGuard.js).
  const bindings = useMemo(() => {
    const map = {};
    tabs.forEach(tab => {
      map[tab.hotkey] = () => setActiveTab(tab.id);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useGlobalHotkey(bindings);

  return (
    <div className="main-tabbed-interface">
      {/* Tab Bar */}
      <div className="main-tab-bar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`main-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={`${tab.label} (${tab.hotkey.toUpperCase()})`}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            <span className="tab-hotkey">{tab.hotkey.toUpperCase()}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="main-tab-content">
        {activeTab === 'nav' && <NavigationPanel />}
        {activeTab === 'ship' && <ShipManagementScreen onClose={() => {}} embedded />}
        {activeTab === 'crew' && <CrewManagementScreen onClose={() => {}} embedded />}
        {activeTab === 'cargo' && <TradeScreen onClose={() => {}} location={null} embedded />}
        {activeTab === 'missions' && <MissionsTab />}
        {activeTab === 'stats' && <StatsTab />}
        {activeTab === 'more' && <MoreTab />}
      </div>
    </div>
  );
};

export default MainTabbedInterface;
